import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SliderSkeleton from './SliderSkeleton';
import ErrorState from './../../ErrorState/ErrorState';
import { useCachedFetch } from './../../../hooks/useCachedFetch';
import './Slider.scss';

const Slider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const slideRefs = useRef([]);
  const intervalRef = useRef(null);
  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  const { data: workData, loading, error, refetch } = useCachedFetch('/data/locales/work.json');

  const [isLoading, setIsLoading] = useState(true);
  const [hasData, setHasData] = useState(false);

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  const reviews = useMemo(() => {
    if (!workData?.WorkData) return [];

    return workData.WorkData
      .filter((item) => item.feedback?.personFeedback)
      .map((item) => ({
        review: item.feedback.personFeedback?.[currentLang] || item.feedback.personFeedback?.en || '',
        name: item.feedback.personName?.[currentLang] || item.feedback.personName?.en || '',
        position: item.feedback.personPosition?.[currentLang] || item.feedback.personPosition?.en || '',
        companyName: item.feedback.personCompany?.[currentLang] || item.feedback.personCompany?.en || '',
      }))
      .filter(review => review.review.trim() !== '')
      .slice(0, 20);
  }, [workData, currentLang]);

  const slides = useMemo(() => {
    return reviews.map((review) => [
      review.review,
      review.name,
      [review.position, review.companyName].filter(Boolean).join(', '),
    ]);
  }, [reviews]);

  useEffect(() => {
    if (loading) return;

    if (error) {
      setHasData(false);
      setIsLoading(false);
      return;
    }

    if (reviews.length > 0) {
      setHasData(true);
    } else {
      setHasData(false);
    }

    setIsLoading(false);
  }, [loading, error, reviews.length]);

  const stopAutoSlide = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startAutoSlide = useCallback(() => {
    if (slides.length > 1) {
      stopAutoSlide();
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % slides.length);
      }, 7000);
    }
  }, [slides.length, stopAutoSlide]);

  const goToSlide = useCallback((index) => {
    stopAutoSlide();
    setCurrentIndex(index);
    startAutoSlide();
  }, [startAutoSlide, stopAutoSlide]);

  useEffect(() => {
    if (hasData && slides.length > 1) {
      startAutoSlide();
    }

    return () => stopAutoSlide();
  }, [hasData, slides.length, startAutoSlide, stopAutoSlide]);

  if (isLoading) {
    return <SliderSkeleton />;
  }

  if (error || !hasData || slides.length === 0) {
    return <ErrorState onRetry={handleRetry} theme={'dark'} />;
  }

  return (
    <div className="slider">
      <div className="slides">
        {slides.map((paragraphs, index) => (
          <div
            key={index}
            ref={(el) => (slideRefs.current[index] = el)}
            className={`slide ${index === currentIndex ? 'active' : ''}`}
          >
            {paragraphs.map((text, pIndex) => (
              <p 
                className={`para__${pIndex + 1}`} 
                key={pIndex}
              >
                {text}
              </p>
            ))}
          </div>
        ))}
      </div>
      {slides.length > 1 && (
        <div className="dots">
          {slides.map((_, index) => (
            <button
              key={index}
              className={`dot ${index === currentIndex ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
              type="button"
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default React.memo(Slider);